import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { IconBrandName } from '@mylighthouse/prism-ember/types/components/prism/icon-brand';
import dayjs from 'dayjs';
import ParityTooltipsTooltipPosTableLoss from 'lumen/components/parity/tooltips/tooltip-pos-table-loss';
import { filterMajorOTAs, filterParities } from 'lumen/utils/parities-filter';
import { formatParity, sortParities } from 'lumen/utils/parity-utils';

const MAX_NB_OF_OTAS_IN_CELL = 3;

interface ParityTablesCellsTdPosOtasWithIssuesSignature {
  Args: {
    cellData: unknown;
    column: unknown;
  };
}

export default class ParityTablesCellsTdPosOtasWithIssues extends Component<ParityTablesCellsTdPosOtasWithIssuesSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];
  @service declare rates: Services['rates'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseRates() {
    const { cellData, column } = this.args;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    const otaIds = column.availableOTAs?.map((ota) => ota.id);

    const baseRates = filterMajorOTAs(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      otaIds,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      cellData.paritiesPOSDict,
      (parity) => parity.is_baserate,
    );

    return baseRates.map((baseRate) => {
      const { channel, currency, extract_datetime, ota } = baseRate;

      return {
        channel: channel as IconBrandName,
        extractDatetimeFromNow: dayjs.utc(extract_datetime).fromNow(),
        extract_datetime,
        rate: this.rates.formatRate(baseRate, this.hotel),
        rateUnconverted: [
          this.rates.formatRateObject(baseRate, false),
          currency,
        ].join(' '),
        source: this.ota.otaIdToPrettyName(ota),
        title: this.ota.otaIdToPrettyName(channel),
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get channelNames() {
    const channelNames = this.formattedChannels.map((channel) => channel.title);
    if (channelNames.length > MAX_NB_OF_OTAS_IN_CELL) {
      return `${channelNames.slice(0, MAX_NB_OF_OTAS_IN_CELL).join(',')}...`;
    }
    if (channelNames.length) {
      return channelNames.join(',');
    }
    return this.intl.t('parity.no_issues');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChannels() {
    // @ts-expect-error: Incorrect type
    if (!this.period.paritiesPOSDict) {
      return [];
    }

    const parities = filterParities(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.period.paritiesPOSDict,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.args.column.searchMajorOtas,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.args.column.availableOTAs,
      (parity) => parity.value !== 0 && parity.position_to_baserate < 0,
    );

    return parities
      .sort(sortParities)
      .slice(0, 15)
      .map((parity) => formatParity(this.ota, this.rates, this.hotel, parity));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isToday() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.period.day.isToday;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get period() {
    return this.args.cellData;
  }

  <template>
    <div
      class={{if this.isToday "td-today"}}
      data-test-id="pos-otas-with-issues-cell"
    >
      <p>{{this.channelNames}}</p>

      <ParityTooltipsTooltipPosTableLoss
        @baseRates={{this.baseRates}}
        @formattedChannels={{this.formattedChannels}}
      />
    </div>
  </template>
}
